import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { FabricCanvas, FabricIEvent } from '../types/fabric';

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 10;

/**
 * Set for a brief window after a manual `zoomCanvasAt` call — lets
 * `DesignerApp.tsx`'s `ResizeObserver` (which re-fits the canvas when the workspace's
 * *layout chrome* resizes it, e.g. opening the Clipart panel) tell that case apart
 * from a resize the zoom itself just caused: `setDimensions` growing the canvas past
 * the workspace's bounds makes the scrollable container grow a scrollbar, which
 * shrinks its own `clientWidth`/`clientHeight` (the scrollbar gutter) and fires that
 * same `ResizeObserver` — which then called `fitToViewport()` and force-reset the
 * zoom the user had just requested back down to whatever fits, on every single
 * zoom-in step that crossed the "now needs a scrollbar" threshold. Confirmed live via
 * a frame-by-frame screen recording: zoom visibly jumped to 42.9% for one frame (with
 * scrollbars) then snapped back to 35.8% (no scrollbars) the very next frame, and
 * every further zoom-in click repeated the same jump-then-reset with no net change —
 * exactly the reported "if I zoom, scrollbar comes and resets the zoom" symptom.
 */
export let suppressAutoFit = false;
let suppressAutoFitTimer: ReturnType<typeof setTimeout> | undefined;
function markManualZoom() {
  suppressAutoFit = true;
  clearTimeout(suppressAutoFitTimer);
  // ResizeObserver callbacks run after layout/paint, on their own queue — a
  // rAF isn't guaranteed to land after that queue drains, so this uses a short
  // real timeout instead. Long enough to outlast the observer firing from this
  // zoom step's own `setDimensions` call, short enough that a *genuine* layout
  // resize (e.g. toggling the Clipart panel) a moment later still re-fits normally.
  suppressAutoFitTimer = setTimeout(() => { suppressAutoFit = false; }, 100);
}

/**
 * This app's canvas isn't an infinite pannable surface — it's a fixed design
 * area whose DOM element is always resized (via `setDimensions`) to exactly
 * `originalWidth/Height * zoom`, so sibling elements (the product photo,
 * design-area guide, grid overlay, sibling-area boxes in
 * `DesignerWorkspace.tsx`) can scale identically using the same
 * `canvas.getZoom()` value and line up pixel-for-pixel with whatever Fabric
 * renders.
 *
 * That alignment only holds if Fabric's own `viewportTransform` never carries
 * a translation — every one of those sibling elements positions itself as
 * `area* * zoom`, with no pan term of its own. An earlier version of this
 * function anchored zoom at the cursor/viewport-center by computing a
 * non-zero `tx`/`ty` so the same canvas-space point stayed under the anchor
 * — but since the canvas's own backing-store size is *also* resized to
 * exactly fit the (unpanned) content, that translation just shifted the
 * rendered objects away from the design-area guide/photo/grid overlays
 * sitting on top of it, by a different amount on every zoom step. That's the
 * real cause of "zooming in/out moves my text and images relative to the
 * placeholder" — confirmed live by reading `canvas.viewportTransform`'s
 * `tx`/`ty` after a zoom-to-cursor step and finding them non-zero whenever
 * the anchor wasn't exactly the canvas's own top-left corner. Fixed by
 * always keeping the transform pure-scale; "zoom toward where you're
 * looking" is instead implemented by scrolling the workspace's scroll
 * container post-resize (see `zoomCanvasKeepingCenter` below), which moves
 * the whole rigid composition (photo + canvas + overlays) together instead
 * of shifting the canvas's content relative to them.
 */
export function zoomCanvasAt(canvas: FabricCanvas, newZoom: number) {
  markManualZoom();
  const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
  const fc = canvas as any;
  const ow = fc._originalWidth  ?? canvas.getWidth()  / canvas.getZoom();
  const oh = fc._originalHeight ?? canvas.getHeight() / canvas.getZoom();

  canvas.setViewportTransform([clamped, 0, 0, clamped, 0, 0]);
  canvas.setDimensions({ width: ow * clamped, height: oh * clamped });
  canvas.renderAll();
  return clamped;
}

/**
 * Zooms, then adjusts the scroll container so whatever was centered in the
 * viewport before the zoom is still centered after — the resize-based model
 * above means zooming in can make the whole composition (photo + canvas)
 * bigger than the viewport, and without this the view would stay pinned to
 * the scroll container's top-left corner instead of staying on whatever the
 * user was actually looking at.
 */
export function zoomCanvasKeepingCenter(canvas: FabricCanvas, newZoom: number, container: HTMLElement | null) {
  const oldZoom = canvas.getZoom();
  if (!container) return zoomCanvasAt(canvas, newZoom);

  const oldCenterX = container.scrollLeft + container.clientWidth / 2;
  const oldCenterY = container.scrollTop + container.clientHeight / 2;

  const applied = zoomCanvasAt(canvas, newZoom);
  const ratio = applied / oldZoom;

  container.scrollLeft = oldCenterX * ratio - container.clientWidth / 2;
  container.scrollTop  = oldCenterY * ratio - container.clientHeight / 2;
  return applied;
}

export function useCanvasZoom(canvas: FabricCanvas | null, containerRef?: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!canvas) return;

    const handleWheel = (opt: FabricIEvent) => {
      const e = opt.e as WheelEvent;
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();

      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const zoom = canvas.getZoom() * delta;
      zoomCanvasKeepingCenter(canvas, zoom, containerRef?.current ?? null);
    };

    canvas.on('mouse:wheel', handleWheel);
    return () => canvas.off('mouse:wheel', handleWheel);
  }, [canvas, containerRef]);
}

/**
 * Alt+drag / middle-mouse-drag panning. Scrolls the workspace's scroll
 * container rather than mutating Fabric's `viewportTransform` — the canvas's
 * own backing store is always sized to exactly fit its (unpanned) content
 * (see the big comment on `zoomCanvasAt` above), so an internal Fabric pan
 * has nothing to reveal; it would only shift rendered objects away from the
 * design-area guide/photo overlays sitting on top of the canvas, the same
 * misalignment bug zoom-to-cursor used to cause. Scrolling the container
 * instead moves the whole rigid composition together, and is also what
 * actually lets you reach the parts of a zoomed-in image that don't fit in
 * the viewport (previously there was no scrollbar and no other way to pan).
 */
export function useCanvasPan(canvas: FabricCanvas | null, containerRef?: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!canvas) return;

    let isPanning = false;
    let lastPos   = { x: 0, y: 0 };

    const onDown = (opt: FabricIEvent) => {
      const e = opt.e as MouseEvent;
      if (e.altKey || e.button === 1) {
        isPanning = true; canvas.selection = false;
        lastPos = { x: e.clientX, y: e.clientY };
      }
    };

    const onMove = (opt: FabricIEvent) => {
      if (!isPanning) return;
      const e = opt.e as MouseEvent;
      const container = containerRef?.current;
      if (container) {
        container.scrollLeft -= e.clientX - lastPos.x;
        container.scrollTop  -= e.clientY - lastPos.y;
      }
      lastPos = { x: e.clientX, y: e.clientY };
    };

    const onUp = () => { isPanning = false; canvas.selection = true; };

    canvas.on('mouse:down', onDown);
    canvas.on('mouse:move', onMove);
    canvas.on('mouse:up',   onUp);

    return () => {
      canvas.off('mouse:down', onDown);
      canvas.off('mouse:move', onMove);
      canvas.off('mouse:up',   onUp);
    };
  }, [canvas, containerRef]);
}
